import { Link } from 'react-router-dom';
import { LoadingSpinner } from '../../components/ui/loading-spinner';
import { TestStatusBadge } from '../../components/test-status-badge';
import {
  useMemberTests,
  type MemberTest,
} from '../../hooks/use-organization-tests';
import type { DateRange } from '../../helpers/get-current-test-period-range';
import { formatTestDate } from '../../helpers/format-test-date';
import { formatTestDuration } from '../../helpers/format-test-duration';

const PANEL_ROW_LIMIT = 10;

interface MemberTestsPanelProps {
  organizationId: number | undefined;
  userId: number;
  range: DateRange;
  isOpen: boolean;
}

function TestRow({ test }: { test: MemberTest }) {
  return (
    <div className="grid grid-cols-[60px_1fr_110px_90px_70px] gap-4 items-center px-4 py-2">
      <span className="text-sm text-muted-foreground">{test.testNumber}</span>
      <span className="text-sm">
        {formatTestDate(test.endedAt, test.isGenerated)}
      </span>
      <TestStatusBadge status={test.status} />
      <span className="text-sm">
        {formatTestDuration(test.startedAt, test.endedAt)}
      </span>
      <span className="text-sm text-right font-medium">{test.score}%</span>
    </div>
  );
}

function TestsList({ tests }: { tests: MemberTest[] }) {
  if (tests.length === 0) {
    return (
      <p className="text-sm text-muted-foreground text-center py-4">
        No tests in this range
      </p>
    );
  }
  return (
    <div>
      <div className="grid grid-cols-[60px_1fr_110px_90px_70px] gap-4 px-4 py-2 text-xs font-medium text-muted-foreground border-b">
        <span>Test #</span>
        <span>Date</span>
        <span>Status</span>
        <span>Time Taken</span>
        <span className="text-right">Score</span>
      </div>
      <div className="divide-y">
        {tests.map((test) => (
          <TestRow key={test.id} test={test} />
        ))}
      </div>
    </div>
  );
}

export function MemberTestsPanel({
  organizationId,
  userId,
  range,
  isOpen,
}: MemberTestsPanelProps) {
  const { data, isLoading, error } = useMemberTests(
    organizationId,
    userId,
    range,
    isOpen,
  );

  if (isLoading) {
    return (
      <div className="flex justify-center py-4">
        <LoadingSpinner />
      </div>
    );
  }
  if (error || !data) {
    return (
      <p className="text-sm text-muted-foreground text-center py-4">
        Could not load tests. Please try again.
      </p>
    );
  }
  return (
    <div className="space-y-2">
      <TestsList tests={data.tests} />
      {data.total > PANEL_ROW_LIMIT && (
        <Link
          to={`/users/${userId}/stats?tab=tests`}
          className="block text-sm text-center text-accent-purple hover:underline"
        >
          View more
        </Link>
      )}
    </div>
  );
}
